import { useEffect, useRef, useState } from "react";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { useProducts } from "@/lib/useProducts";
import { WHATSAPP_URL } from "@/lib/site";

const EMPTY = { product_slug: "", quantity: "", destination_port: "", incoterm: "", company: "", contact_name: "",
  country: "", email: "", phone: "", message: "", website: "" };

const TEXT_FIELDS = [
  ["company", "Company name", "e.g. Al Noor Foodstuff Trading LLC", true],
  ["contact_name", "Your name", "Optional", false],
  ["country", "Country", "e.g. United Arab Emirates", true],
  ["destination_port", "Destination port", "e.g. Jebel Ali, Dammam, Rotterdam", true],
  ["quantity", "Quantity", "e.g. 2 x 40 ft reefer containers", true],
  ["email", "Business email", "you@company.com", true, "email"],
  ["phone", "WhatsApp / phone (with country code)", "+971 50 123 4567", true, "tel"],
];

const Field = ({ f, value, onChange }) => (
  <div className="space-y-2">
    <Label htmlFor={f[0]}>{f[1]}{f[3] && <span className="text-amber-700"> *</span>}</Label>
    <Input id={f[0]} type={f[4] || "text"} placeholder={f[2]} value={value} required={f[3]}
      onChange={(e) => onChange(f[0], e.target.value)} data-testid={`quote-${f[0].replace("_", "-")}-input`} className="h-11 bg-white" />
  </div>
);

const SelectField = ({ id, label, value, onChange, items }) => (
  <div className="space-y-2">
    <Label>{label}<span className="text-amber-700"> *</span></Label>
    <Select value={value} onValueChange={(v) => onChange(id, v)}>
      <SelectTrigger className="h-11 bg-white" data-testid={`quote-${id.replace("_", "-")}-select`}><SelectValue placeholder="Choose" /></SelectTrigger>
      <SelectContent>
        {items.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`quote-option-${v}`}>{l}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);

const Success = ({ onReset }) => {
  const ref = useRef(null);
  useEffect(() => {
    ref.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, []);
  return (
  <div ref={ref} className="rounded-lg border border-emerald-200 bg-emerald-50 p-8 space-y-4" data-testid="quote-success">
    <CheckCircle2 className="h-8 w-8 text-emerald-700" />
    <h3 className="text-2xl font-semibold text-emerald-950">Thank you. Your request is with our team.</h3>
    <p className="text-stone-700">We usually call back within one working day (India time) to confirm quantity, packing and shipping dates. For anything urgent, message us on WhatsApp.</p>
    <div className="flex flex-wrap gap-3">
      <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="rounded-full bg-green-500 text-white px-5 py-2.5 text-sm font-medium" data-testid="quote-success-whatsapp">Open WhatsApp</a>
      <button onClick={onReset} className="rounded-full border border-stone-300 px-5 py-2.5 text-sm font-medium hover:bg-white" data-testid="quote-success-new-btn">Send another request</button>
    </div>
  </div>
  );
};

export const QuoteForm = ({ defaultProduct = "" }) => {
  const { data: products = [] } = useProducts();
  const [form, setForm] = useState({ ...EMPTY, product_slug: defaultProduct });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.product_slug || !form.incoterm) return toast.error("Please choose a product and an Incoterm");
    setBusy(true);
    try {
      await api.post("/inquiries", form);
      setDone(true);
    } catch (err) {
      toast.error(errMsg(err, "Could not send your request. Please try WhatsApp."));
    } finally {
      setBusy(false);
    }
  };

  if (done) return <Success onReset={() => { setForm({ ...EMPTY }); setDone(false); }} />;
  return (
    <form onSubmit={submit} className="space-y-6" data-testid="quote-form" noValidate={false}>
      <div className="grid gap-5 sm:grid-cols-2">
        <SelectField id="product_slug" label="Product" value={form.product_slug} onChange={set}
          items={products.map((p) => [p.slug, p.name])} />
        <SelectField id="incoterm" label="Incoterm" value={form.incoterm} onChange={set}
          items={[["FOB", "FOB (Indian port)"], ["CIF", "CIF (your port)"]]} />
        {TEXT_FIELDS.map((f) => <Field key={f[0]} f={f} value={form[f[0]]} onChange={set} />)}
      </div>
      <div className="space-y-2">
        <Label htmlFor="message">Anything else? (packing, grade, shipping month)</Label>
        <Textarea id="message" rows={4} value={form.message} onChange={(e) => set("message", e.target.value)}
          className="bg-white" data-testid="quote-message-input" />
      </div>
      <div className="absolute -left-[9999px] h-0 overflow-hidden" aria-hidden="true">
        <label htmlFor="website">Website</label>
        <input id="website" name="website" tabIndex={-1} autoComplete="off" value={form.website}
          onChange={(e) => set("website", e.target.value)} data-testid="quote-honeypot" />
      </div>
      <p className="text-xs text-stone-500">We do not publish prices. You get a written quote after a short call to confirm your requirements.</p>
      <button type="submit" disabled={busy} data-testid="quote-submit-btn"
        className="inline-flex items-center gap-2 rounded-full bg-emerald-900 hover:bg-emerald-800 disabled:opacity-60 text-white px-8 py-3.5 font-medium transition-colors">
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} Send quote request
      </button>
    </form>
  );
};
